import axios from 'axios';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { firebaseAuth } from '@/lib/firebase';
import { useImpersonationStore } from '@/shared/impersonation/impersonation.store';
import { useAuthStore } from '@/modules/auth/presentation/store/auth.store';
import { AuthStorageDatasource, extractApiErrorCode } from '@react-apps/auth';
import type { StoredAuth } from '@/modules/auth/domain/entities/auth.entity';

/**
 * Resolve o usuário atual aguardando o Firebase restaurar a sessão. No primeiro
 * request após um reload, `currentUser` ainda é null (restauração é assíncrona) —
 * sem isto, a request sai sem token e o gateway responde 401. Esperamos o primeiro
 * `onAuthStateChanged` (que dispara assim que o Firebase inicializa).
 */
function resolveCurrentUser(): Promise<User | null> {
  if (firebaseAuth.currentUser) {
    return Promise.resolve(firebaseAuth.currentUser);
  }
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
}

/**
 * Código de erro de domínio do backend (`{ error: 'ACCOUNT_NOT_FOUND', ... }`).
 * Reexportado daqui porque este é o ponto que o resto do app já conhece — a
 * implementação mora em `@react-apps/auth`, compartilhada com o user-dashboard.
 */
export { extractApiErrorCode } from '@react-apps/auth';

const BASE_URL = import.meta.env.VITE_API_BASE_URL as string;
/** Scope deste app — o `x-scope-id` de toda chamada que não pede outro. */
export const SCOPE_ID = import.meta.env.VITE_SCOPE_ID as string;

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'x-scope-id': SCOPE_ID,
  },
});

apiClient.interceptors.request.use(async (config) => {
  const user = await resolveCurrentUser();
  if (user) {
    const token = await user.getIdToken();
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  // "Atuar como": injeta a sessão de suporte. O gateway repassa este header
  // (não está na lista de strip) e o backend só o honra se a sessão for deste
  // ator, deste scope e ainda estiver valendo — forjá-lo aqui não dá privilégio.
  const sessionId = useImpersonationStore.getState().sessionId;
  if (sessionId) {
    config.headers.set('x-act-as-session-id', sessionId);
  }

  return config;
});

const authStorage = new AuthStorageDatasource<StoredAuth>();

/**
 * Rede de segurança: um 401 do gateway/back significa **sessão inválida** (token
 * ausente/expirado/revogado) — não é falta de permissão (isso é 403). Derruba a
 * sessão local, a máscara e desloga do Firebase; o `onAuthStateChanged` do AuthGuard
 * cascateia o redirect pro /sign-in, em vez de deixar a UI numa sessão fantasma.
 *
 * A exceção é o 401 `IMPERSONATION_SESSION_ENDED`: quem acabou foi a sessão de
 * suporte (venceu ou foi encerrada), não o login do ator. Cai só a máscara.
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (extractApiErrorCode(error) === 'IMPERSONATION_SESSION_ENDED') {
      useImpersonationStore.getState().stopImpersonation();
    } else if (error?.response?.status === 401) {
      useImpersonationStore.getState().stopImpersonation();
      authStorage.clear();
      useAuthStore.getState().clearAuth();
      void signOut(firebaseAuth);
    }
    return Promise.reject(error);
  },
);
